import { createAsyncThunk } from '@reduxjs/toolkit';
import type { AsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import type { AxiosRequestConfig } from 'axios';
import { getToken } from 'src/helpers/utils';


interface ThunkRequestConfig {
  method: string;
  url: string;
  contentType?: string;
  data?: unknown;
  signal?: AbortSignal;
  shouldAddSocketId?: boolean;
  integrationApiKey?: string;
  withCredentials?: boolean;
}

interface ThunkRejectValue {
  actionType?: string;
  status?: number;
  [key: string]: unknown;
}

// Format Request Options
export function requestOptions(
  method: string,
  url: string,
  params: unknown,
  contentType: string | undefined,
  data: unknown,
  token: string | null,
  signal: AbortSignal | undefined,
  shouldAddSocketId: boolean | undefined,
  integrationApiKey: string | undefined,
): AxiosRequestConfig {
  const socketId = sessionStorage?.getItem('socket_id');

  const options: AxiosRequestConfig = {
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      ...(socketId && shouldAddSocketId ? { 'X-Socket-ID': socketId } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    method,
    url,
    params,
    data,
    signal,
  };
  if (method !== 'POST' && method !== 'PUT' && method !== 'PATCH' && params) {
    options.params = params;
  } else if (contentType === 'json') {
    (options.headers as Record<string, string>)['Content-Type'] = 'application/json';
    options.params = '';
    options.data = params;
  } else if (contentType === 'form-data') {
    (options.headers as Record<string, string>)['Content-Type'] = 'multipart/form-data';
    options.data = params;
    options.params = '';
  } else {
    options.data = params;
  }
  if (integrationApiKey) {
    (options.headers as Record<string, string>)['x-api-key'] = integrationApiKey;
  }

  return options;
}

// Common Request

export const request = (
  options: ThunkRequestConfig,
  name: string,
  extraData?: Record<string, unknown>
): AsyncThunk<
  unknown, unknown, { rejectValue: ThunkRejectValue }
> => createAsyncThunk<
  unknown, unknown, { rejectValue: ThunkRejectValue }
>(name, async (payload, thunkAPI) => {
  try {
    const res = await axios({
      ...requestOptions(
        options.method,
        options.url,
        payload,
        options.contentType,
        options.data,
        getToken(),
        options.signal,
        options.shouldAddSocketId,
        options.integrationApiKey,
      ),
      withCredentials: options.withCredentials,
    });
    if (extraData) {
      return {...res.data, ...extraData}
    }
    return res.data;
  } catch (error) {
    const axiosErr = error as { response?: { status?: number; data?: Record<string, unknown> } };
    if (axiosErr?.response?.status === 401) {
      try {
        if (process.env.NODE_ENV === 'development') {
          // biome-ignore lint/suspicious/noConsole: necessary logging
          console.warn('Session expired, please login again');
        }
      } catch (err) {
        // biome-ignore lint/suspicious/noConsole: necessary logging
        console.error(err);
      }
    } else if (axios.isCancel(error)) {
      // Request was canceled, no need to handle this error
      return thunkAPI.rejectWithValue({ actionType: 'canceled' });
    } else {
      return thunkAPI.rejectWithValue({
        ...(axiosErr?.response?.data || {}),
        status: axiosErr?.response?.status,
      });
    }
  }
  return null;
});

export function getBaseUrl(): string {
  return process.env.NEXT_PUBLIC_APP_API_URL ?? '';
}
